import React, { useMemo } from 'react';
import { Gantt, Willow } from '@svar-ui/react-gantt';
import { GanttChartSquare } from 'lucide-react';
import i18n from '../../../i18n';
import '@svar-ui/react-gantt/all.css';

const toLocalDate = (isoString, fallback = new Date()) => {
    if (!isoString) return fallback;
    return new Date(isoString.slice(0, 10) + 'T00:00:00');
};

const GanttChart = ({ workPackages }) => {
    const tasks = useMemo(() => {
        if (!workPackages || workPackages.length === 0) return [];

        return workPackages.map((wp) => ({
            id: wp.id,
            text: wp.name,
            start: toLocalDate(wp.start_date),
            end: toLocalDate(wp.end_date),
            progress: 0,
            type: 'task',
            etis: wp.etis,
            start_formatted: wp.start_date ? wp.start_date.slice(0, 10) : '',
            end_formatted: wp.end_date ? wp.end_date.slice(0, 10) : '',
        }));
    }, [workPackages]);

    const totalEtis = useMemo(
        () => tasks.reduce((sum, task) => sum + (Number(task.etis) || 0), 0),
        [tasks]
    );

    const columns = [
        { id: 'text', header: i18n.t('components.Gantt.columns.workPackage'), width: 220 },
        { id: 'start_formatted', header: i18n.t('components.Gantt.columns.start'), width: 100 },
        { id: 'end_formatted', header: i18n.t('components.Gantt.columns.end'), width: 100 },
        { id: 'etis', header: i18n.t('components.Gantt.columns.etis'), width: 70 },
    ];

    if (tasks.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                <GanttChartSquare size={40} className="txt-subtle mb-3" />
                <p className="fw-semibold txt-main mb-1">{i18n.t('components.Gantt.emptyTitle')}</p>
                <p className="txt-subtle mb-0" style={{ fontSize: '13px' }}>
                    {i18n.t('components.Gantt.emptySubtitle')}
                </p>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <div
                className="d-flex justify-content-between align-items-center px-3 py-2 mb-2"
                style={{ borderBottom: 'var(--border-thin)' }}
            >
                <div className="d-flex align-items-center gap-2">
                    <GanttChartSquare size={18} className="txt-subtle" />
                    <span className="fw-semibold txt-main">{i18n.t('components.Gantt.scheduleTitle')}</span>
                    <span className="badge bg-light text-dark border">{tasks.length}</span>
                </div>
                <span className="txt-subtle" style={{ fontSize: '13px' }}>
                    {i18n.t('components.Gantt.total')}: <strong className="txt-main">{totalEtis.toFixed(2)} ETIs</strong>
                </span>
            </div>

            <div style={{ width: '100%', height: '500px', padding: '0 20px 20px' }}>
                <Willow>
                    <Gantt
                        key={tasks.length}
                        tasks={tasks}
                        scales={[
                            {
                                unit: 'month',
                                step: 1,
                                format: (date) =>
                                    new Intl.DateTimeFormat(i18n.language || 'pt-PT', { month: 'long', year: 'numeric' }).format(date),
                            },
                            {
                                unit: 'day',
                                step: 1,
                                format: (date) => date.getDate(),
                            },
                        ]}
                        columns={columns}
                        readonly={true}
                        style={{ height: '100%' }}
                    />
                </Willow>
            </div>
        </div>
    );
};

export default GanttChart;